import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Container, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toApiError } from '@/api/errorHandler';
import { ProductFormDialog, type ProductFormValues } from '@/components/Admin';
import { ConfirmationDialog } from '@/components/Shared/ConfirmationDialog';
import { PageHeader } from '@/components/Shared/PageHeader';
import {
  BulkActionsToolbar,
  ProductEmptyState,
  ProductFilters,
  ProductSearch,
  ProductTable,
} from '@/components/products';
import { defaultProductRegistryPageSize } from '@/config/productRegistry';
import {
  useCreateProduct,
  useDeleteProduct,
  useProduct,
  useUpdateProduct,
} from '@/hooks/useProducts';
import { useProductRegistry } from '@/hooks/useProductRegistry';
import type { ProductActionId, RegistryProduct } from '@/types/productRegistry';
import { useProductRegistryPermission } from '@/utils/productRegistryPermissions';

type SortableColumn =
  | 'name'
  | 'category'
  | 'status'
  | 'environment'
  | 'activeUsers'
  | 'lastModified';

export function ProductManagementPage() {
  const { t } = useTranslation('entity-portal');
  const canCreate = useProductRegistryPermission('PRODUCT_CREATE');
  const canEdit = useProductRegistryPermission('PRODUCT_EDIT');
  const canDelete = useProductRegistryPermission('PRODUCT_DELETE');
  const canDeploy = useProductRegistryPermission('PRODUCT_DEPLOY');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultProductRegistryPageSize);
  const [sortBy, setSortBy] = useState<SortableColumn>('lastModified');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [deleteTargetProduct, setDeleteTargetProduct] =
    useState<RegistryProduct | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const queryParams = useMemo(
    () => ({
      search,
      status,
      category,
      page: page + 1,
      pageSize,
      sortBy,
      sortDirection,
    }),
    [category, page, pageSize, search, sortBy, sortDirection, status],
  );

  const { data, isLoading, isError, refetch } = useProductRegistry(queryParams);
  const { data: editingProduct } = useProduct(editingProductId ?? undefined);
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  const products = useMemo(() => data?.items ?? [], [data?.items]);
  const total = useMemo(() => data?.total ?? 0, [data?.total]);
  const isMutationPending =
    createProductMutation.isPending ||
    updateProductMutation.isPending ||
    deleteProductMutation.isPending;

  const editingRegistryProduct = useMemo(
    () => products.find((product) => product.id === editingProductId),
    [editingProductId, products],
  );

  const productFormInitialValues = useMemo<ProductFormValues>(() => {
    if (dialogMode === 'edit') {
      return {
        name: editingProduct?.name ?? editingRegistryProduct?.name ?? '',
        description:
          editingProduct?.description ??
          editingRegistryProduct?.description ??
          '',
        iframeUrl:
          editingProduct?.iframeUrl ?? editingRegistryProduct?.iframeUrl ?? '',
        openInNewTab: editingProduct?.openInNewTab ?? false,
      };
    }

    return {
      name: '',
      description: '',
      iframeUrl: '',
      openInNewTab: false,
    };
  }, [dialogMode, editingProduct, editingRegistryProduct]);

  const handleSortChange = useCallback(
    (column: SortableColumn) => {
      if (sortBy === column) {
        setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }

      setSortBy(column);
      setSortDirection('asc');
    },
    [sortBy],
  );

  const handleSelectRow = useCallback(
    (productId: string, selected: boolean) => {
      setSelectedIds((current) =>
        selected
          ? [...new Set([...current, productId])]
          : current.filter((id) => id !== productId),
      );
    },
    [],
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      setSelectedIds(selected ? products.map((product) => product.id) : []);
    },
    [products],
  );

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setStatus('all');
    setCategory('all');
    setPage(0);
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    setDialogMode('create');
    setEditingProductId(null);
    setSubmitError(null);
    setIsProductDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((productId: string) => {
    setDialogMode('edit');
    setEditingProductId(productId);
    setSubmitError(null);
    setIsProductDialogOpen(true);
  }, []);

  const handleCloseProductDialog = useCallback(() => {
    if (isMutationPending) {
      return;
    }

    setIsProductDialogOpen(false);
    setEditingProductId(null);
    setSubmitError(null);
  }, [isMutationPending]);

  const handleProductSubmit = useCallback(
    async (values: ProductFormValues) => {
      setSubmitError(null);

      try {
        if (dialogMode === 'create') {
          await createProductMutation.mutateAsync({
            name: values.name,
            description: values.description,
            iframeUrl: values.iframeUrl,
            openInNewTab: values.openInNewTab,
          });
        } else {
          if (!editingProductId) {
            throw new Error('Missing product id for update operation.');
          }

          await updateProductMutation.mutateAsync({
            productId: editingProductId,
            data: {
              name: values.name,
              description: values.description,
              iframeUrl: values.iframeUrl,
              openInNewTab: values.openInNewTab,
            },
          });
        }

        handleCloseProductDialog();
        await refetch();
      } catch (error) {
        setSubmitError(toApiError(error, t).message);
      }
    },
    [
      createProductMutation,
      dialogMode,
      editingProductId,
      handleCloseProductDialog,
      refetch,
      t,
      updateProductMutation,
    ],
  );

  const handleDeleteProduct = useCallback(async () => {
    if (!deleteTargetProduct) {
      return;
    }

    try {
      await deleteProductMutation.mutateAsync(deleteTargetProduct.id);
      setDeleteTargetProduct(null);
      setSelectedIds((current) =>
        current.filter((id) => id !== deleteTargetProduct.id),
      );
      await refetch();
    } catch {
      setDeleteTargetProduct(null);
    }
  }, [deleteProductMutation, deleteTargetProduct, refetch]);

  const handleDeactivateProduct = useCallback(
    async (product: RegistryProduct) => {
      try {
        await updateProductMutation.mutateAsync({
          productId: product.id,
          data: {
            isActive: false,
          },
        });
        await refetch();
      } catch {
        // The update mutation handles user-facing failure via existing notifications.
      }
    },
    [refetch, updateProductMutation],
  );

  const handleProductAction = useCallback(
    (actionId: ProductActionId, product: RegistryProduct) => {
      if (actionId === 'edit') {
        if (canEdit) {
          handleOpenEditDialog(product.id);
        }
        return;
      }

      if (actionId === 'delete') {
        if (canDelete) {
          setDeleteTargetProduct(product);
        }
        return;
      }

      if (actionId === 'deactivate') {
        if (canDeploy) {
          void handleDeactivateProduct(product);
        }
        return;
      }

      if (actionId === 'openEmbeddedApp') {
        if (product.iframeUrl) {
          window.open(product.iframeUrl, '_blank', 'noopener,noreferrer');
        }
      }
    },
    [
      canDelete,
      canDeploy,
      canEdit,
      handleDeactivateProduct,
      handleOpenEditDialog,
    ],
  );

  const handleBulkAction = useCallback((_actionId: string) => {
    void _actionId;
    // Placeholder for future bulk operations.
  }, []);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        bgcolor: 'background.default',
      }}
    >
      <Container
        maxWidth={false}
        sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}
      >
        <PageHeader
          breadcrumb={[
            { label: t('admin.adminDashboardBreadcrumbPortal') },
            { label: t('productRegistry.productRegistryBreadcrumbProducts') },
          ]}
          title={t('productRegistry.productRegistryTitle')}
          subtitle={t('productRegistry.productRegistrySubtitle')}
          breadcrumbAriaLabel={t('productRegistry.productRegistryBreadcrumbLabel')}
        />

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          sx={{ mb: 2.5 }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ flex: 1 }}
          >
            <ProductSearch value={search} onChange={setSearch} />
            <ProductFilters
              status={status}
              category={category}
              onStatusChange={(value) => {
                setStatus(value);
                setPage(0);
              }}
              onCategoryChange={(value) => {
                setCategory(value);
                setPage(0);
              }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <BulkActionsToolbar
              selectedCount={selectedIds.length}
              onAction={handleBulkAction}
            />
            {canCreate ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isMutationPending}
                onClick={handleOpenCreateDialog}
              >
                {t('productRegistry.productRegistryAddProduct')}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {isError ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={<Button onClick={() => refetch()}>{t('common.retry')}</Button>}
          >
            {t('product.productsLoadErrorFallback')}
          </Alert>
        ) : null}

        {!isLoading && products.length === 0 ? (
          <ProductEmptyState
            onClearFilters={handleClearFilters}
            onAddProduct={handleOpenCreateDialog}
          />
        ) : (
          <ProductTable
            products={products}
            total={total}
            page={page}
            pageSize={pageSize}
            selectedIds={selectedIds}
            sortBy={sortBy}
            sortDirection={sortDirection}
            isLoading={isLoading}
            onPageChange={setPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(0);
            }}
            onSortChange={handleSortChange}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onAction={handleProductAction}
          />
        )}

        <ProductFormDialog
          open={isProductDialogOpen}
          mode={dialogMode}
          initialValues={productFormInitialValues}
          submitError={submitError}
          isSubmitting={isMutationPending}
          onClose={handleCloseProductDialog}
          onSubmit={handleProductSubmit}
        />

        <ConfirmationDialog
          open={Boolean(deleteTargetProduct)}
          title={t('productRegistry.productRegistryActions.delete')}
          description={
            deleteTargetProduct
              ? `${t('common.delete')} ${t(deleteTargetProduct.name, {
                  defaultValue: deleteTargetProduct.name,
                })}?`
              : ''
          }
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          destructive
          onClose={() => setDeleteTargetProduct(null)}
          onConfirm={() => {
            void handleDeleteProduct();
          }}
        />
      </Container>
    </Box>
  );
}
